import { DefaultModalProps, useModals } from '@frontend/modal';
import { useAccountRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { ReactNode, useEffect, useState } from 'react';

import { NotificationPreferencesProps } from './notification-preferences.component';

interface ViewProps {
    openModal: (
        id: string,
        render: () => ReactNode,
        wrapperOptions?: {
            handleCloseCallback?: () => void;
            backdropDismissible?: boolean;
        },
        modalOptions?: Omit<DefaultModalProps, 'show' | 'handleClose'>
    ) => string;
    keys: (keyof NotificationPreference)[];
    notificationSettings: NotificationPreference | null;
    accountNotificationSettings: NotificationPreference | null;
    notificationSettingId: string | null;
    setNotificationSettings: (settings: NotificationPreference) => void;
}
export const useNotificationPreferences = (props: NotificationPreferencesProps): ViewProps => {
    const { openModal } = useModals();
    const { currentAccount } = useAccountRepository();
    const [notificationSettingId, setNotificationSettingId] = useState<string | null>(null);
    const [notificationSettings, setNotificationSettings] = useState<NotificationPreference | null>(null);
    const [accountNotificationSettings, setAccountNotificationSettings] = useState<NotificationPreference | null>(null);

    const onFetch = (accountNotificationSettings: NotificationPreference) => {
        SettingClient.fetchSettings({ account_id: props.object.account_id, entity_id: props.object.id }).then((res) => {
            const foundNotificationSettings = res.results.find((setting) => setting.key === SettingKey.DEFAULT_CHANNELS);
            if (!foundNotificationSettings) {
                setNotificationSettings({
                    email_channel: accountNotificationSettings.email_channel,
                    mobile_channel: accountNotificationSettings.mobile_channel,
                    push_channel: accountNotificationSettings.push_channel,
                    whatsapp_channel: accountNotificationSettings.whatsapp_channel
                });
                return;
            }
            setNotificationSettingId(foundNotificationSettings.id);
            setNotificationSettings({
                email_channel: (foundNotificationSettings.value as string[]).includes('email'),
                mobile_channel: (foundNotificationSettings.value as string[]).includes('sms'),
                push_channel: (foundNotificationSettings.value as string[]).includes('push'),
                whatsapp_channel: (foundNotificationSettings.value as string[]).includes('whatsapp')
            });
        });
    };

    useEffect(() => {
        SettingClient.fetchSettings({ account_id: currentAccount, entity_id: currentAccount }).then((res) => {
            const foundNotificationSettings = res.results.find((setting) => setting.key === SettingKey.DEFAULT_CHANNELS);
            if (!foundNotificationSettings) {
                setAccountNotificationSettings({
                    email_channel: false,
                    mobile_channel: false,
                    push_channel: false,
                    whatsapp_channel: false
                });
                return;
            }
            setAccountNotificationSettings({
                email_channel: (foundNotificationSettings.value as string[]).includes('email'),
                mobile_channel: (foundNotificationSettings.value as string[]).includes('sms'),
                push_channel: (foundNotificationSettings.value as string[]).includes('push'),
                whatsapp_channel: (foundNotificationSettings.value as string[]).includes('whatsapp')
            });
        });
    }, [currentAccount, props.object.account_id, props.object.id]);

    useEffect(() => {
        if (accountNotificationSettings) onFetch(accountNotificationSettings);
    }, [accountNotificationSettings, props.object]);

    return {
        openModal,
        keys: accountNotificationSettings
            ? (Object.keys(accountNotificationSettings) as (keyof NotificationPreference)[]).filter((key) => accountNotificationSettings[key])
            : [],
        notificationSettings,
        accountNotificationSettings,
        notificationSettingId,
        setNotificationSettings
    };
};

export interface NotificationPreference {
    email_channel: boolean;
    mobile_channel: boolean;
    push_channel: boolean;
    whatsapp_channel: boolean;
}
